import classNames from 'classnames';
import { asText, Content, isFilled } from '@prismicio/client';

import {
  Box,
  Card,
  Icon,
  Heading,
  AutoGrid,
  CardHero,
  CardFooter,
  Typography,
  CardHeader,
  CardContent,
} from '@preventx/oxygen';

// Services
import { fontColour } from '@/services/utils/colour';
import { retrieveIcon } from '@/services/utils/icons';
import { applicationEnv } from '@/services/env/shared';

// Components
import { Link } from '@/components/Actions/Link';
import { RichText } from '@preventx/prismic-services';

type WithIconFeaturesProps = {
  sliceId: string;
  slice: Content.RegionSliceWithIconFeatures;
};

export const WithIconFeatures = ({ slice, sliceId }: WithIconFeaturesProps) => {
  const { primary } = slice;
  const { title, caption, features, title_alignment } = primary;

  const { WebsiteUrl } = applicationEnv();

  return (
    <>
      <Box
        component="div"
        className={classNames('headline', {
          'align-center':
            title_alignment === 'Default' || title_alignment === 'Centre',
        })}
      >
        <Box component="div" addFlow>
          <Heading component="h2" addHeadlineTitle>
            {asText(title)}
          </Heading>

          {isFilled.richText(caption) && (
            <Typography component="p" className="headline__sub">
              {asText(caption)}
            </Typography>
          )}
        </Box>
      </Box>

      <AutoGrid component="div" layout="50-50">
        {(features ?? []).map((feature, index) => {
          const { icon, title, content, icon_colour, is_title_hidden } =
            feature;

          if (!isFilled.select(icon)) {
            return null;
          }

          const iconValue = retrieveIcon(icon);
          const colour = fontColour(icon_colour);

          return (
            <Card
              component="article"
              key={`region-image-features-${sliceId}-${index + 1}`}
            >
              {feature.feature_layout === 'Default' && (
                <>
                  {iconValue && (
                    <CardHero component="div">
                      <Icon
                        size="2x"
                        icon={iconValue}
                        fontColour={colour ?? undefined}
                      />
                    </CardHero>
                  )}

                  <CardHeader addSROnly={is_title_hidden}>
                    <Heading component="h3">{asText(title)}</Heading>
                  </CardHeader>

                  {isFilled.richText(content) && (
                    <CardContent addFlow>
                      <RichText
                        content={content}
                        variant="default"
                        websiteUrl={WebsiteUrl}
                      />
                    </CardContent>
                  )}
                </>
              )}

              {feature.feature_layout === 'As Content Only' && (
                <CardContent addFlow>
                  <Icon
                    size="2x"
                    icon={iconValue}
                    fontColour={colour ?? undefined}
                  />

                  <Heading component="h3" addSROnly={is_title_hidden}>
                    {asText(title)}
                  </Heading>

                  {isFilled.richText(content) && (
                    <RichText
                      content={content}
                      variant="default"
                      websiteUrl={WebsiteUrl}
                    />
                  )}
                </CardContent>
              )}

              {isFilled.link(feature.link) && (
                <CardFooter>
                  <Link
                    as="default"
                    type="button"
                    retainSearchParams
                    href={feature.link}
                    className="card__action"
                  >
                    {feature.link.text}
                  </Link>
                </CardFooter>
              )}
            </Card>
          );
        })}
      </AutoGrid>
    </>
  );
};
